import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import config from '../config';

const About = () => {
  return (
    <>
      <SEO 
        title="About Us - BlogCraft"
        description="Learn about BlogCraft - your trusted source for quality content on technology, lifestyle, business, travel, and more. Discover our mission, values, and commitment to excellence."
        keywords="about blogcraft, about us, blog platform, content creators, quality content, technology blog, lifestyle articles"
        image={`${config.SITE_URL}/og-image.jpg`}
        type="website"
        url={`${config.SITE_URL}/about`}
      />

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About BlogCraft</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Your trusted source for quality content on technology, lifestyle, business, travel, and more.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Our Story */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-4">
                Founded in 2024, BlogCraft emerged from a simple vision: to create a platform where quality content meets passionate readers. 
                We noticed a gap in the digital landscape for well-researched, engaging, and authentic content that truly adds value to readers' lives.
              </p>
              <p className="mb-4">
                What started as a small team of passionate writers has grown into a diverse community of content creators, 
                experts, and thought leaders dedicated to sharing knowledge and experiences across various domains.
              </p>
              <p>
                Today, BlogCraft stands as a testament to the power of quality content and community engagement, 
                serving thousands of readers monthly with insightful articles, practical tips, and inspiring stories.
              </p>
            </div>
          </section>

          {/* Our Mission */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
              <p className="text-lg text-gray-800 leading-relaxed">
                To empower readers with high-quality, well-researched content that educates, inspires, and entertains, 
                while providing a platform for writers to share their expertise and connect with a global audience.
              </p>
            </div>
          </section>

          {/* Our Values */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-blue-600 font-bold text-xl">Q</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Quality</h3>
                <p className="text-gray-600 text-sm">
                  We prioritize well-researched, accurate, and engaging content that provides real value to our readers.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-green-600 font-bold text-xl">A</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Authenticity</h3>
                <p className="text-gray-600 text-sm">
                  We believe in genuine voices and real experiences, creating content that resonates with our audience.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-purple-600 font-bold text-xl">D</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Diversity</h3>
                <p className="text-gray-600 text-sm">
                  We celebrate diverse perspectives and voices, covering a wide range of topics and viewpoints.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <span className="text-orange-600 font-bold text-xl">C</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Community</h3>
                <p className="text-gray-600 text-sm">
                  We foster a supportive community where writers and readers can connect, learn, and grow together.
                </p>
              </div>
            </div>
          </section>

          {/* Our Team - Commented Out */}
          {/* <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Meet Our Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-600">JD</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">John Doe</h3>
                <p className="text-gray-600 text-sm mb-2">Founder & Editor-in-Chief</p>
                <p className="text-gray-500 text-sm">
                  Passionate about quality content and building communities around shared interests.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-600">JS</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Jane Smith</h3>
                <p className="text-gray-600 text-sm mb-2">Content Director</p>
                <p className="text-gray-500 text-sm">
                  Expert in content strategy and ensuring the highest quality standards for all publications.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-gray-600">MJ</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">Mike Johnson</h3>
                <p className="text-gray-600 text-sm mb-2">Technical Lead</p>
                <p className="text-gray-500 text-sm">
                  Ensuring our platform runs smoothly and provides the best user experience possible.
                </p>
              </div>
            </div>
          </section> */}

          {/* Stats - Commented Out */}
          {/* <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">By the Numbers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                <p className="text-gray-600">Published Articles</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">50+</div>
                <p className="text-gray-600">Contributing Writers</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">10K+</div>
                <p className="text-gray-600">Monthly Readers</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600 mb-2">15+</div>
                <p className="text-gray-600">Content Categories</p>
              </div>
            </div>
          </section> */}

          {/* Policy Links */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Legal & Policies</h2>
            <div className="bg-gray-50 rounded-2xl p-8">
              <p className="text-gray-700 mb-6">
                At BlogCraft, we're committed to transparency and protecting your rights. 
                Please review our policies to understand how we operate and protect your information.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link 
                  to="/privacy-policy" 
                  className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition text-center"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">Privacy Policy</h3>
                  <p className="text-sm text-gray-600">How we collect and protect your data</p>
                </Link>
                <Link 
                  to="/terms-of-service" 
                  className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition text-center"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">Terms of Service</h3>
                  <p className="text-sm text-gray-600">Rules and guidelines for using our platform</p>
                </Link>
                <Link 
                  to="/cookie-policy" 
                  className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition text-center"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">Cookie Policy</h3>
                  <p className="text-sm text-gray-600">How we use cookies and tracking technologies</p>
                </Link>
                <Link 
                  to="/disclaimer" 
                  className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition text-center"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">Disclaimer</h3>
                  <p className="text-sm text-gray-600">Important information about our content and services</p>
                </Link>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Join Our Community</h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Whether you're a reader looking for quality content or a writer wanting to share your voice, 
              BlogCraft welcomes you to join our growing community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/blog" 
                className="px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition"
              >
                Explore Articles
              </Link>
              <Link 
                to="/contact" 
                className="px-8 py-3 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition"
              >
                Contact Us
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default About;
